import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, TextField, Snackbar, Paper, CircularProgress, Box } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import MDBox from "components/MDBox";
import AuthService from "services/AuthService";
import UserService from "services/UserService";
import { useAuth } from "context/authContext";

const emptyProfileForm = {
  Id: null,
  Name: "",
  Email: "",
  Main: false,
  SupplierId: "",
  Role: { Id: "" },
  newName: "",
  newPassword: "",
  confirmPassword: "",
};

export default function TabProfile() {
  const { updateUser, isAuthenticated } = useAuth();
  const [user, setUser] = useState(emptyProfileForm);
  const [isEdit, setIsEdit] = useState(false);
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const init = async () => {
      const authenticated = await isAuthenticated();
      if (!authenticated) {
        navigate(`/login`);
        return;
      }

      await loadData();
    };

    init();
  }, [navigate]);

  const loadData = async () => {
    try {
      const currentUser = AuthService.getCurrentUser();

      if (!currentUser) {
        setUser(emptyProfileForm);
        return;
      }

      setUser({
        ...currentUser,
        newName: currentUser.Name || "",
        newPassword: '',
        confirmPassword: '',
      });
    }
    catch (error) {
      console.error("Error loading user data:", error);
      navigate(`/login`);
    }
    finally {
      setLoading(false);
    }
  }

  const passwordCompare = (value) => user.newPassword === value;

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleUserEdited = (field, value) => {
    setUser({ ...user, [field]: value })
    setIsEdit(true);
  }

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (!passwordCompare(user.confirmPassword)) {
      setAlert({ type: 'error', message: 'As senhas devem ser iguais!' });
      setOpen(true);
      return;
    }

    try {
      setLoading(true);
      const editUser = { Name: user.newName };

      if (!user.newPassword) {
        delete editUser.Password;
      } else {
        editUser.Password = user.newPassword;
      }

      await UserService
        .updateProfile(editUser)
        .then((res) => {
          const userData = res.data.data[0];
          setUser({ ...userData, newName: userData.Name, newPassword: '', confirmPassword: '' });
          updateUser();
          setIsEdit(false);
        })
        .catch((e) => {
          throw e;
        });
      setAlert({ type: 'success', message: 'Dados salvos!' });
      setOpen(true);
    } catch (error) {
      setAlert({ type: 'error', message: `Erro ao atualizar dados: ${error.response.data.error}` });
      setOpen(true);
      console.error("Erro ao atualizar dados:", error);
    }
    finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  const snackBar = (
    <Snackbar open={open} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <MuiAlert onClose={handleClose} severity={alert.type} sx={{ width: "100%" }}>
        {alert.message}
      </MuiAlert>
    </Snackbar>
  )

  return (
    <MDBox mt={2}>
      <Paper
        elevation={3}
        sx={{
          backgroundColor: '#fff',
          padding: 2,
          borderRadius: 3,
          width: '100%',
        }}
      >
        <TextField
          fullWidth
          label="Nome"
          type="text"
          variant="outlined"
          value={user?.newName || ""}
          onChange={(e) => handleUserEdited("newName", e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Email"
          type="email"
          variant="outlined"
          value={user?.Email || ""}
          disabled
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Senha"
          type="password"
          variant="outlined"
          value={user?.newPassword || ""}
          onChange={(e) =>
            handleUserEdited("newPassword", e.target.value)
          }
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Confirmar Senha"
          type="password"
          variant="outlined"
          value={user?.confirmPassword || ""}
          onChange={(e) =>
            handleUserEdited("confirmPassword", e.target.value)
          }
          sx={{ mb: 2 }}
        />

        <Button
          variant="contained"
          onClick={handlePasswordReset}
          disabled={!isEdit}
        >
          <MDBox color="white">Salvar</MDBox>
        </Button>
        {snackBar}
      </Paper>
    </MDBox>
  );
}